import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  AlertTriangle,
  Ban,
  Check,
  Download,
  EyeOff,
  Link2,
  Link2Off,
  Rocket,
  ScanLine,
  Trash2,
} from 'lucide-react';
import {
  DEAD_FISCAL_STATUSES,
  FISCAL_CHECK_STATUS_LABELS,
  FISCAL_DIVERGENCE_STATUS_LABELS,
  FISCAL_DIVERGENCE_STATUSES,
  FISCAL_DIVERGENCE_TYPE_LABELS,
  FISCAL_DOCUMENT_KIND_LABELS,
  FISCAL_DOCUMENT_ORIGIN_LABELS,
  FISCAL_SITUATION_LABELS,
  FISCAL_STATUS_LABELS,
  FISCAL_TARGET_STATUS_LABELS,
  formatCents,
  formatDocument,
  formatQty,
  type FiscalCheckResult,
  type FiscalDivergence,
  type FiscalDocument,
  type FiscalTrace,
  type Paginated,
  type Receiving,
  type SuggestedOrder,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { blank, formatDate, formatDateTime } from '@/lib/format';
import { T } from '@/design/tokens';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { Attachments } from '@/components/Attachments';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { ConfirmDialog, Modal } from '@/components/Modal';
import { Field, FormSection } from '@/components/FormSection';
import { ErrorState, LoadingState } from '@/components/states';
import { useAuth } from '@/lib/auth-context';
import { LancarDocumentoDrawer } from './DocumentoLancamento';
import {
  CHECK_TONE,
  DIVERGENCE_TONE,
  FISCAL_TONE,
  LISTA_DOCUMENTOS,
  SITUATION_TONE,
  TARGET_TONE,
  formatAccessKey,
} from './documentos-shared';

/**
 * Detalhe do documento fiscal.
 *
 * Uma página, não um drawer: aqui moram o resumo, os itens, os vínculos com
 * pedido e recebimento, a conferência, as divergências, os dois destinos de
 * integração, os arquivos e o histórico — mais a trilha de onde a nota veio
 * (REQ → COT → PED → REC). Num drawer isso vira rolagem infinita.
 */
export function DocumentoDetalhePage({ id }: { id: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [lancando, setLancando] = useState(false);
  const [vinculando, setVinculando] = useState<'pedido' | 'recebimento' | null>(null);
  const [confirmando, setConfirmando] = useState<'ignore' | 'cancel-fiscal' | 'delete' | null>(null);
  const [novaDivergencia, setNovaDivergencia] = useState(false);

  const query = useQuery({
    queryKey: ['fiscal-document', id],
    queryFn: () => api.get<FiscalDocument>(`/fiscal-documents/${id}`),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['fiscal-document', id] });
    void queryClient.invalidateQueries({ queryKey: ['fiscal-documents'] });
    void queryClient.invalidateQueries({ queryKey: ['financial-entries'] });
    void queryClient.invalidateQueries({ queryKey: ['stock-balances'] });
  };

  const checkMutation = useMutation({
    mutationFn: () => api.post<FiscalCheckResult>(`/fiscal-documents/${id}/check`, {}),
    onSuccess: refresh,
  });
  const ignoreMutation = useMutation({
    mutationFn: (reason: string) => api.post(`/fiscal-documents/${id}/ignore`, { reason }),
    onSuccess: () => {
      setConfirmando(null);
      refresh();
    },
  });
  const cancelMutation = useMutation({
    mutationFn: (reason: string) => api.post(`/fiscal-documents/${id}/cancel-fiscal`, { reason }),
    onSuccess: () => {
      setConfirmando(null);
      refresh();
    },
  });
  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/fiscal-documents/${id}`),
    onSuccess: () => {
      setConfirmando(null);
      void queryClient.invalidateQueries({ queryKey: ['fiscal-documents'] });
      void navigate({ to: LISTA_DOCUMENTOS });
    },
  });

  const baixarXml = async () => {
    const arquivo = await api.get<{ filename: string; xml: string }>(
      `/fiscal-documents/${id}/xml`,
    );
    const blob = new Blob([arquivo.xml], { type: 'application/xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = arquivo.filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (query.isPending) return <LoadingState label="Abrindo o documento…" />;
  if (query.isError) {
    return <ErrorState message={errorMessage(query.error)} onRetry={() => void query.refetch()} />;
  }

  const doc = query.data;
  // Rejeitada também é nota morta: sem isto ela vinha sem o alerta e com o
  // botão Conferir ligado, que a API recusa com 409.
  const cancelada = DEAD_FISCAL_STATUSES.includes(doc.fiscalStatus);
  const abertas = doc.divergences.filter(
    (d) => d.status === 'ABERTA' || d.status === 'EM_ANALISE',
  );
  const podeLancar =
    !cancelada &&
    doc.status !== 'IGNORADO' &&
    abertas.length === 0 &&
    (doc.financialStatus === 'PENDENTE' || doc.stockStatus === 'PENDENTE');

  return (
    <>
      <WorkspaceHeader
        back={{ to: LISTA_DOCUMENTOS, label: 'Notas de entrada' }}
        title={`${doc.code} · ${FISCAL_DOCUMENT_KIND_LABELS[doc.kind]} ${doc.number}${
          doc.series ? `/${doc.series}` : ''
        }`}
        meta={[
          { label: 'Fornecedor', value: doc.supplierTradeName ?? doc.supplierName },
          { label: 'Total', value: formatCents(doc.totalCents) },
        ]}
        actions={
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {doc.hasXml && (
              <Button variant="ghost" icon={Download} onClick={() => void baixarXml()}>
                Baixar XML
              </Button>
            )}
            {can('documentos-fiscais:conferir') && !cancelada && doc.status !== 'IGNORADO' && (
              <Button
                variant="ghost"
                icon={ScanLine}
                loading={checkMutation.isPending}
                onClick={() => checkMutation.mutate()}
              >
                Conferir
              </Button>
            )}
            {can('documentos-fiscais:lancar') && podeLancar && (
              <Button icon={Rocket} onClick={() => setLancando(true)}>
                Lançar
              </Button>
            )}
          </div>
        }
      />

      <div className="workspace__body">
        {cancelada && (
          <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
            <strong>Nota {FISCAL_STATUS_LABELS[doc.fiscalStatus].toLowerCase()} na SEFAZ.</strong>{' '}
            O registro fica guardado para consulta, mas não vira conta a pagar nem entrada de
            estoque. {doc.cancelReason ? `Motivo: ${doc.cancelReason}` : ''}
            {doc.financialStatus === 'LANCADO' && (
              <> A conta a pagar já criada continua valendo — cancele-a no Financeiro.</>
            )}
          </div>
        )}

        {checkMutation.isSuccess && (
          <div
            role="status"
            style={{
              marginBottom: 14,
              padding: '10px 14px',
              borderRadius: 10,
              border: `1px solid ${checkMutation.data.opened > 0 ? T.amber : T.mareLight}`,
              background: checkMutation.data.opened > 0 ? T.amberTint : T.mareTint,
              fontSize: 12.5,
            }}
          >
            {checkMutation.data.message}
          </div>
        )}
        {checkMutation.isError && (
          <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
            {errorMessage(checkMutation.error)}
          </div>
        )}

        <SecaoResumo doc={doc} />
        <Trilha id={id} />
        <SecaoItens doc={doc} />
        <SecaoPedidos
          doc={doc}
          onVincular={() => setVinculando('pedido')}
          onChanged={refresh}
          canLink={can('documentos-fiscais:vincular')}
        />
        <SecaoRecebimentos
          doc={doc}
          onVincular={() => setVinculando('recebimento')}
          onChanged={refresh}
          canLink={can('documentos-fiscais:vincular')}
        />
        <SecaoDivergencias
          doc={doc}
          onChanged={refresh}
          onNova={() => setNovaDivergencia(true)}
          canTreat={can('documentos-fiscais:conferir')}
        />
        <SecaoIntegracao doc={doc} />
        <SecaoArquivos doc={doc} canEdit={can('documentos-fiscais:editar')} />
        <SecaoHistorico id={id} />

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 20 }}>
          {can('documentos-fiscais:editar') && !cancelada && (
            <Button variant="ghost" icon={Ban} onClick={() => setConfirmando('cancel-fiscal')}>
              Marcar como cancelada
            </Button>
          )}
          {can('documentos-fiscais:editar') && doc.status !== 'LANCADO' && doc.status !== 'IGNORADO' && (
            <Button variant="ghost" icon={EyeOff} onClick={() => setConfirmando('ignore')}>
              Ignorar
            </Button>
          )}
          {can('documentos-fiscais:excluir') &&
            doc.financialStatus !== 'LANCADO' &&
            doc.stockStatus !== 'LANCADO' && (
              <Button variant="ghost" icon={Trash2} onClick={() => setConfirmando('delete')}>
                Excluir
              </Button>
            )}
        </div>
      </div>

      {lancando && (
        <LancarDocumentoDrawer
          document={doc}
          onClose={() => setLancando(false)}
          onPosted={() => {
            setLancando(false);
            refresh();
          }}
        />
      )}

      {vinculando === 'pedido' && (
        <VincularPedidoModal
          doc={doc}
          onClose={() => setVinculando(null)}
          onLinked={() => {
            setVinculando(null);
            refresh();
          }}
        />
      )}

      {vinculando === 'recebimento' && (
        <VincularRecebimentoModal
          doc={doc}
          onClose={() => setVinculando(null)}
          onLinked={() => {
            setVinculando(null);
            refresh();
          }}
        />
      )}

      {novaDivergencia && (
        <NovaDivergenciaModal
          doc={doc}
          onClose={() => setNovaDivergencia(false)}
          onCreated={() => {
            setNovaDivergencia(false);
            refresh();
          }}
        />
      )}

      <ConfirmDialog
        open={confirmando !== null}
        title={
          confirmando === 'cancel-fiscal'
            ? 'Marcar a nota como cancelada'
            : confirmando === 'delete'
              ? 'Excluir o documento'
              : 'Ignorar o documento'
        }
        message={
          confirmando === 'cancel-fiscal'
            ? 'O registro é preservado e fica marcado como CANCELADA: não vira conta a pagar nem entrada de estoque. Se já existir lançamento, ele continua valendo e precisa ser cancelado no Financeiro.'
            : confirmando === 'delete'
              ? 'O documento sai da lista. Só é possível enquanto nada foi lançado a partir dele.'
              : 'A nota some da fila de pendências sem virar lançamento nenhum. Use para nota de outra empresa ou enviada por engano.'
        }
        requireReason={confirmando !== 'delete'}
        destructive={confirmando === 'delete'}
        onClose={() => setConfirmando(null)}
        confirmLabel={
          confirmando === 'cancel-fiscal'
            ? 'Marcar cancelada'
            : confirmando === 'delete'
              ? 'Excluir'
              : 'Ignorar'
        }
        loading={ignoreMutation.isPending || cancelMutation.isPending || deleteMutation.isPending}
        error={
          ignoreMutation.isError
            ? errorMessage(ignoreMutation.error)
            : cancelMutation.isError
              ? errorMessage(cancelMutation.error)
              : deleteMutation.isError
                ? errorMessage(deleteMutation.error)
                : undefined
        }
        onConfirm={(reason) => {
          if (confirmando === 'cancel-fiscal') cancelMutation.mutate(reason ?? '');
          else if (confirmando === 'delete') deleteMutation.mutate();
          else ignoreMutation.mutate(reason ?? '');
        }}
      />
    </>
  );
}

/* --------------------------------- seções -------------------------------- */

function Secao({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="card" style={{ padding: 16, marginBottom: 16 }}>
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 12,
          marginBottom: 12,
        }}
      >
        <div>
          <h2 style={{ fontSize: 14, margin: 0 }}>{title}</h2>
          {description && (
            <p className="muted" style={{ fontSize: 12, margin: '2px 0 0' }}>
              {description}
            </p>
          )}
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}

function Campo({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div className="muted" style={{ fontSize: 11 }}>
        {label}
      </div>
      <div className={mono ? 'mono' : undefined} style={{ fontSize: 13 }}>
        {value}
      </div>
    </div>
  );
}

function SecaoResumo({ doc }: { doc: FiscalDocument }) {
  return (
    <Secao title="Resumo" description="O que a nota diz e em que pé está cada frente">
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
        <StatusBadge kind={SITUATION_TONE[doc.situation]}>
          {FISCAL_SITUATION_LABELS[doc.situation]}
        </StatusBadge>
        <StatusBadge kind={FISCAL_TONE[doc.fiscalStatus]}>
          SEFAZ: {FISCAL_STATUS_LABELS[doc.fiscalStatus]}
        </StatusBadge>
        <StatusBadge kind={CHECK_TONE[doc.checkStatus]}>
          {FISCAL_CHECK_STATUS_LABELS[doc.checkStatus]}
        </StatusBadge>
        <StatusBadge kind={TARGET_TONE[doc.financialStatus]}>
          Financeiro: {FISCAL_TARGET_STATUS_LABELS[doc.financialStatus]}
        </StatusBadge>
        <StatusBadge kind={TARGET_TONE[doc.stockStatus]}>
          Estoque: {FISCAL_TARGET_STATUS_LABELS[doc.stockStatus]}
        </StatusBadge>
      </div>

      <div className="grid-2">
        <div>
          <Campo label="Emitente" value={doc.supplierName} />
          <Campo
            label="CNPJ / CPF"
            value={doc.supplierDocument ? formatDocument(doc.supplierDocument) : '—'}
            mono
          />
          <Campo label="Inscrição estadual" value={blank(doc.supplierStateRegistration)} mono />
          <Campo label="Destinatário" value={blank(doc.recipientName)} />
          <Campo label="Origem do dado" value={FISCAL_DOCUMENT_ORIGIN_LABELS[doc.origin]} />
        </div>
        <div>
          <Campo label="Modelo / série" value={`${blank(doc.model)} · ${blank(doc.series)}`} mono />
          <Campo label="Chave de acesso" value={formatAccessKey(doc.accessKey)} mono />
          <Campo label="Protocolo" value={blank(doc.protocol)} mono />
          <Campo label="Emissão" value={formatDate(doc.issueDate)} mono />
          <Campo
            label="Entrada no parque"
            value={doc.entryDate ? formatDate(doc.entryDate) : '—'}
            mono
          />
        </div>
      </div>

      <div
        className="mono"
        style={{
          display: 'flex',
          gap: 18,
          flexWrap: 'wrap',
          marginTop: 10,
          paddingTop: 10,
          borderTop: `1px solid ${T.line}`,
          fontSize: 12.5,
        }}
      >
        <span>produtos {formatCents(doc.productsCents)}</span>
        <span>frete {formatCents(doc.freightCents)}</span>
        <span>desconto {formatCents(doc.discountCents)}</span>
        <span>seguro {formatCents(doc.insuranceCents)}</span>
        <span>outras {formatCents(doc.otherCents)}</span>
        <span>impostos {formatCents(doc.taxCents)}</span>
        <strong>total {formatCents(doc.totalCents)}</strong>
      </div>

      {doc.notes && (
        <p style={{ fontSize: 12.5, marginTop: 10, whiteSpace: 'pre-wrap' }}>{doc.notes}</p>
      )}
    </Secao>
  );
}

/** REQ → COT → PED → REC → NF-e: de onde esta nota veio. */
function Trilha({ id }: { id: string }) {
  const query = useQuery({
    queryKey: ['fiscal-document', id, 'trace'],
    queryFn: () => api.get<FiscalTrace>(`/fiscal-documents/${id}/trace`),
  });

  if (query.isPending || query.isError) return null;
  const etapas = [
    { label: 'Requisição', linhas: query.data.requisitions },
    { label: 'Cotação', linhas: query.data.quotations },
    { label: 'Pedido', linhas: query.data.orders },
    { label: 'Recebimento', linhas: query.data.receivings },
  ].filter((etapa) => etapa.linhas.length > 0);

  if (etapas.length === 0) return null;

  return (
    <Secao title="De onde veio" description="A trilha da compra até esta nota">
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'stretch' }}>
        {etapas.map((etapa) => (
          <div
            key={etapa.label}
            className="card"
            style={{ padding: 10, minWidth: 160, borderColor: T.mareLight }}
          >
            <div className="muted" style={{ fontSize: 11 }}>
              {etapa.label}
            </div>
            {etapa.linhas.map((linha) => (
              <div key={linha.id}>
                <div className="mono" style={{ fontSize: 12.5, fontWeight: 600 }}>
                  {linha.code}
                </div>
                {linha.title && (
                  <div className="muted" style={{ fontSize: 11 }}>
                    {linha.title}
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
        <div className="card" style={{ padding: 10, minWidth: 160, borderColor: T.mareDark }}>
          <div className="muted" style={{ fontSize: 11 }}>
            Documento
          </div>
          <div className="mono" style={{ fontSize: 12.5, fontWeight: 600 }}>
            esta nota
          </div>
        </div>
      </div>
    </Secao>
  );
}

function SecaoItens({ doc }: { doc: FiscalDocument }) {
  if (doc.items.length === 0) {
    return (
      <Secao title="Itens" description="Documento sem itens — recibo, boleto e contrato não têm">
        <p className="muted" style={{ fontSize: 12.5 }}>
          Nada a listar.
        </p>
      </Secao>
    );
  }

  return (
    <Secao title={`Itens (${doc.items.length})`} description="Como vieram no XML da nota">
      <div style={{ overflowX: 'auto' }}>
        <table className="table" style={{ fontSize: 12.5 }}>
          <thead>
            <tr>
              <th style={{ width: 40 }}>#</th>
              <th>Descrição</th>
              <th style={{ width: 110 }}>Cód. forn.</th>
              <th style={{ width: 90 }}>NCM</th>
              <th style={{ width: 110 }}>Item estoque</th>
              <th style={{ width: 110, textAlign: 'right' }}>Qtde</th>
              <th style={{ width: 110, textAlign: 'right' }}>Unitário</th>
              <th style={{ width: 110, textAlign: 'right' }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {doc.items.map((item) => (
              <tr key={item.id}>
                <td className="mono">{item.sequence}</td>
                <td>{item.description}</td>
                <td className="mono">{blank(item.supplierCode)}</td>
                <td className="mono">{blank(item.ncm)}</td>
                <td className="mono">{blank(item.sku)}</td>
                <td className="mono" style={{ textAlign: 'right' }}>
                  {formatQty(item.quantityMilli)} {item.unit}
                </td>
                <td className="mono" style={{ textAlign: 'right' }}>
                  {formatCents(item.unitPriceCents)}
                </td>
                <td className="mono" style={{ textAlign: 'right' }}>
                  {formatCents(item.totalCents)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Secao>
  );
}

function SecaoPedidos({
  doc,
  onVincular,
  onChanged,
  canLink,
}: {
  doc: FiscalDocument;
  onVincular: () => void;
  onChanged: () => void;
  canLink: boolean;
}) {
  const unlink = useMutation({
    mutationFn: (orderId: string) =>
      api.delete(`/fiscal-documents/${doc.id}/orders/${orderId}`),
    onSuccess: onChanged,
  });

  return (
    <Secao
      title="Pedido de compra"
      description="Uma nota pode cobrir mais de um pedido, e um pedido vir em mais de uma nota"
      action={
        canLink ? (
          <Button small icon={Link2} onClick={onVincular}>
            Vincular pedido
          </Button>
        ) : undefined
      }
    >
      {unlink.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 10 }}>
          {errorMessage(unlink.error)}
        </div>
      )}
      {doc.orders.length === 0 ? (
        <p className="muted" style={{ fontSize: 12.5 }}>
          Nenhum pedido vinculado. Sem vínculo, a conferência não tem com o que comparar — e a nota
          pode dar entrada de estoque por conta própria.
        </p>
      ) : (
        doc.orders.map((link) => (
          <div
            key={link.id}
            className="card"
            style={{
              padding: 10,
              marginBottom: 8,
              display: 'flex',
              justifyContent: 'space-between',
              gap: 10,
              alignItems: 'center',
            }}
          >
            <div>
              <div className="mono" style={{ fontSize: 13, fontWeight: 600 }}>
                {link.purchaseOrderCode}
              </div>
              <div className="muted" style={{ fontSize: 11.5 }}>
                {blank(link.supplierName)} · {blank(link.costCenterName)} ·{' '}
                {formatCents(link.totalCents)}
              </div>
            </div>
            {canLink && (
              <Button
                small
                variant="ghost"
                icon={Link2Off}
                loading={unlink.isPending}
                onClick={() => unlink.mutate(link.purchaseOrderId)}
              >
                Desvincular
              </Button>
            )}
          </div>
        ))
      )}
    </Secao>
  );
}

function SecaoRecebimentos({
  doc,
  onVincular,
  onChanged,
  canLink,
}: {
  doc: FiscalDocument;
  onVincular: () => void;
  onChanged: () => void;
  canLink: boolean;
}) {
  const unlink = useMutation({
    mutationFn: (receivingId: string) =>
      api.delete(`/fiscal-documents/${doc.id}/receivings/${receivingId}`),
    onSuccess: onChanged,
  });

  return (
    <Secao
      title="Recebimento"
      description="A quantidade física verdadeira: o que a portaria ACEITOU"
      action={
        canLink ? (
          <Button small icon={Link2} onClick={onVincular}>
            Vincular recebimento
          </Button>
        ) : undefined
      }
    >
      {unlink.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 10 }}>
          {errorMessage(unlink.error)}
        </div>
      )}
      {doc.receivings.length === 0 ? (
        <p className="muted" style={{ fontSize: 12.5 }}>
          Nenhum recebimento vinculado.
        </p>
      ) : (
        doc.receivings.map((link) => (
          <div
            key={link.id}
            className="card"
            style={{
              padding: 10,
              marginBottom: 8,
              display: 'flex',
              justifyContent: 'space-between',
              gap: 10,
              alignItems: 'center',
            }}
          >
            <div>
              <div className="mono" style={{ fontSize: 13, fontWeight: 600 }}>
                {link.receivingCode}
              </div>
              <div className="muted" style={{ fontSize: 11.5 }}>
                {blank(link.purchaseOrderCode)} · {blank(link.locationName)} ·{' '}
                {formatDate(link.receivedAt)} · {formatCents(link.totalCents)}
              </div>
            </div>
            {canLink && (
              <Button
                small
                variant="ghost"
                icon={Link2Off}
                loading={unlink.isPending}
                onClick={() => unlink.mutate(link.receivingId)}
              >
                Desvincular
              </Button>
            )}
          </div>
        ))
      )}
    </Secao>
  );
}

function SecaoDivergencias({
  doc,
  onChanged,
  onNova,
  canTreat,
}: {
  doc: FiscalDocument;
  onChanged: () => void;
  onNova: () => void;
  canTreat: boolean;
}) {
  return (
    <Secao
      title={`Divergências (${doc.divergences.length})`}
      description="O que a nota cobra e a compra não confirma — por escrito, com tratamento"
      action={
        canTreat ? (
          <Button small variant="ghost" icon={AlertTriangle} onClick={onNova}>
            Abrir divergência
          </Button>
        ) : undefined
      }
    >
      {doc.divergences.length === 0 ? (
        <p className="muted" style={{ fontSize: 12.5 }}>
          Nenhuma divergência. Rode a conferência para comparar a nota com o pedido e o
          recebimento.
        </p>
      ) : (
        doc.divergences.map((d) => (
          <DivergenciaCard key={d.id} divergence={d} onChanged={onChanged} canTreat={canTreat} />
        ))
      )}
    </Secao>
  );
}

function DivergenciaCard({
  divergence,
  onChanged,
  canTreat,
}: {
  divergence: FiscalDivergence;
  onChanged: () => void;
  canTreat: boolean;
}) {
  const [tratando, setTratando] = useState(false);
  const [status, setStatus] = useState(divergence.status);
  const [resolution, setResolution] = useState(divergence.resolution ?? '');

  const mutation = useMutation({
    mutationFn: () =>
      api.patch(`/fiscal-documents/divergences/${divergence.id}`, {
        status,
        resolution: resolution.trim() || null,
      }),
    onSuccess: () => {
      setTratando(false);
      onChanged();
    },
  });

  const aberta = divergence.status === 'ABERTA' || divergence.status === 'EM_ANALISE';

  return (
    <div className="card" style={{ padding: 12, marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <strong style={{ fontSize: 13 }}>
          {FISCAL_DIVERGENCE_TYPE_LABELS[divergence.type]}
          {divergence.automatic ? '' : ' · registrada à mão'}
        </strong>
        <StatusBadge kind={DIVERGENCE_TONE[divergence.status]}>
          {FISCAL_DIVERGENCE_STATUS_LABELS[divergence.status]}
        </StatusBadge>
      </div>

      <div style={{ fontSize: 12.5, marginTop: 6 }}>{divergence.description}</div>

      {(divergence.expected || divergence.found) && (
        <div className="mono" style={{ fontSize: 12, marginTop: 4, color: T.inkFaint }}>
          esperado {blank(divergence.expected)} · encontrado {blank(divergence.found)}
        </div>
      )}
      {(divergence.purchaseOrderCode || divergence.receivingCode) && (
        <div className="mono" style={{ fontSize: 11.5, marginTop: 2, color: T.inkFaint }}>
          {divergence.purchaseOrderCode ? `pedido ${divergence.purchaseOrderCode}` : ''}
          {divergence.receivingCode ? ` · recebimento ${divergence.receivingCode}` : ''}
        </div>
      )}

      {divergence.resolution && (
        <div style={{ fontSize: 12, marginTop: 6, color: T.inkSoft }}>
          {divergence.resolution}
          {divergence.resolvedByName ? ` — ${divergence.resolvedByName}` : ''}
          {divergence.resolvedAt ? ` · ${formatDateTime(divergence.resolvedAt)}` : ''}
        </div>
      )}

      {canTreat && !tratando && aberta && (
        <Button small variant="ghost" icon={Check} onClick={() => setTratando(true)}>
          Tratar
        </Button>
      )}

      {tratando && (
        <div style={{ marginTop: 10 }}>
          {mutation.isError && (
            <div className="login__alert" role="alert" style={{ marginBottom: 8 }}>
              {errorMessage(mutation.error)}
            </div>
          )}
          <Field label="Situação" htmlFor={`div-status-${divergence.id}`}>
            <select
              id={`div-status-${divergence.id}`}
              className="input"
              value={status}
              onChange={(e) => setStatus(e.target.value as typeof status)}
            >
              {FISCAL_DIVERGENCE_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {FISCAL_DIVERGENCE_STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Como foi resolvida"
            htmlFor={`div-res-${divergence.id}`}
            hint="Obrigatório para fechar a divergência."
          >
            <textarea
              id={`div-res-${divergence.id}`}
              className="input"
              rows={2}
              value={resolution}
              onChange={(e) => setResolution(e.target.value)}
            />
          </Field>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button small loading={mutation.isPending} onClick={() => mutation.mutate()}>
              Salvar
            </Button>
            <Button small variant="ghost" onClick={() => setTratando(false)}>
              Cancelar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function SecaoIntegracao({ doc }: { doc: FiscalDocument }) {
  return (
    <Secao
      title="Integração"
      description="Financeiro e Estoque — cada um lançado uma única vez, com a origem guardada"
    >
      <div className="grid-2">
        <div>
          <Campo
            label="Financeiro"
            value={
              <StatusBadge kind={TARGET_TONE[doc.financialStatus]}>
                {FISCAL_TARGET_STATUS_LABELS[doc.financialStatus]}
              </StatusBadge>
            }
          />
          <Campo label="Conta a pagar" value={blank(doc.financialEntryCode)} mono />
        </div>
        <div>
          <Campo
            label="Estoque"
            value={
              <StatusBadge kind={TARGET_TONE[doc.stockStatus]}>
                {FISCAL_TARGET_STATUS_LABELS[doc.stockStatus]}
              </StatusBadge>
            }
          />
          <div className="muted" style={{ fontSize: 12 }}>
            {doc.stockStatus === 'COBERTO'
              ? 'A entrada física foi feita pelo recebimento — a nota não movimenta estoque.'
              : doc.stockStatus === 'LANCADO'
                ? 'A nota deu entrada direta: compra sem pedido e sem portaria.'
                : doc.stockStatus === 'BLOQUEADO'
                  ? 'Nota cancelada: nada entra no estoque por ela.'
                  : 'Ainda não entrou em lugar nenhum.'}
          </div>
        </div>
      </div>
    </Secao>
  );
}

function SecaoArquivos({ doc, canEdit }: { doc: FiscalDocument; canEdit: boolean }) {
  return (
    <Secao
      title="XML e arquivos"
      description="O XML original fica guardado; DANFE, boleto e comprovante entram como anexo"
    >
      <p className="muted" style={{ fontSize: 12.5, marginBottom: 10 }}>
        {doc.hasXml
          ? 'XML original guardado — use "Baixar XML" no topo da página.'
          : 'Este documento não tem XML (entrou à mão ou por upload).'}
      </p>
      <Attachments entity="FiscalDocument" entityId={doc.id} canEdit={canEdit} />
    </Secao>
  );
}

function SecaoHistorico({ id }: { id: string }) {
  const query = useQuery({
    queryKey: ['fiscal-document', id, 'timeline'],
    queryFn: () =>
      api.get<
        Array<{ id: string; action: string; userName: string | null; at: string; reason: string | null }>
      >(`/fiscal-documents/${id}/timeline`),
  });

  const rotulo: Record<string, string> = {
    CREATE: 'Documento registrado',
    UPDATE: 'Documento alterado',
    APPROVE: 'Conferência / lançamento',
    REJECT: 'Cancelamento / ignorado',
    DELETE: 'Documento excluído',
  };

  return (
    <Secao title="Histórico" description="Tudo que aconteceu com este documento, e por quem">
      {query.isPending ? (
        <LoadingState label="Carregando o histórico…" />
      ) : query.isError ? (
        <ErrorState message={errorMessage(query.error)} />
      ) : query.data.length === 0 ? (
        <p className="muted" style={{ fontSize: 13 }}>
          Nada registrado ainda.
        </p>
      ) : (
        <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {query.data.map((linha) => (
            <li
              key={linha.id}
              style={{ borderLeft: `2px solid ${T.line}`, paddingLeft: 12, paddingBottom: 14 }}
            >
              <div style={{ fontSize: 12, fontWeight: 600 }}>
                {rotulo[linha.action] ?? linha.action}
              </div>
              <div style={{ fontSize: 12 }}>{blank(linha.userName)}</div>
              <div className="muted" style={{ fontSize: 11 }}>
                {formatDateTime(linha.at)}
              </div>
              {linha.reason && <div style={{ fontSize: 12, marginTop: 2 }}>{linha.reason}</div>}
            </li>
          ))}
        </ol>
      )}
    </Secao>
  );
}

/* -------------------------------- vínculos -------------------------------- */

/**
 * Vincular pedido.
 *
 * O sistema SUGERE (mesmo CNPJ, data próxima, valor que bate) e escreve o
 * motivo de cada palpite. Confirmar é ato de gente: vínculo automático no
 * pedido errado joga a despesa no centro de custo de outra obra.
 */
function VincularPedidoModal({
  doc,
  onClose,
  onLinked,
}: {
  doc: FiscalDocument;
  onClose: () => void;
  onLinked: () => void;
}) {
  const [purchaseOrderId, setPurchaseOrderId] = useState('');

  const query = useQuery({
    queryKey: ['fiscal-document', doc.id, 'suggested-orders'],
    queryFn: () =>
      api.get<{ data: SuggestedOrder[] }>(`/fiscal-documents/${doc.id}/suggested-orders`),
    select: (r) => r.data,
  });

  const mutation = useMutation({
    mutationFn: () => api.post(`/fiscal-documents/${doc.id}/orders`, { purchaseOrderId }),
    onSuccess: onLinked,
  });

  return (
    <Modal
      open
      onClose={onClose}
      wide
      title="Vincular pedido de compra"
      description="Confirme qual pedido é esta nota — o sistema só palpita"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={!purchaseOrderId}
            loading={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            Vincular
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}

      {query.isPending ? (
        <LoadingState label="Procurando pedidos…" />
      ) : query.isError ? (
        <ErrorState message={errorMessage(query.error)} />
      ) : query.data.length === 0 ? (
        <p className="muted" style={{ fontSize: 12.5 }}>
          Nenhum pedido deste fornecedor perto desta data. Confira o cadastro do emitente: sem
          amarrar o CNPJ da nota a uma pessoa cadastrada, não há como sugerir.
        </p>
      ) : (
        <div role="radiogroup" aria-label="Pedidos sugeridos" style={{ display: 'grid', gap: 8 }}>
          {query.data.map((o) => {
            const ativo = o.purchaseOrderId === purchaseOrderId;
            return (
              <button
                key={o.purchaseOrderId}
                type="button"
                role="radio"
                aria-checked={ativo}
                disabled={o.linked}
                onClick={() => setPurchaseOrderId(o.purchaseOrderId)}
                className="card"
                style={{
                  textAlign: 'left',
                  padding: 12,
                  cursor: o.linked ? 'default' : 'pointer',
                  opacity: o.linked ? 0.6 : 1,
                  borderColor: ativo ? T.mareDark : T.line,
                  background: ativo ? T.mareTint : undefined,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <strong className="mono" style={{ fontSize: 13 }}>
                    {o.purchaseOrderCode}
                  </strong>
                  <span className="mono" style={{ fontSize: 12 }}>
                    {formatCents(o.totalCents)}
                  </span>
                </div>
                <div style={{ fontSize: 12.5, marginTop: 2 }}>{blank(o.supplierName)}</div>
                <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                  {o.linked ? 'já vinculado · ' : ''}
                  {o.reasons.join(' · ') || 'sem sinal forte'}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </Modal>
  );
}

function VincularRecebimentoModal({
  doc,
  onClose,
  onLinked,
}: {
  doc: FiscalDocument;
  onClose: () => void;
  onLinked: () => void;
}) {
  const [receivingId, setReceivingId] = useState('');
  const [search, setSearch] = useState('');

  const query = useQuery({
    queryKey: ['receivings', 'para-documento', doc.personId, search],
    queryFn: () =>
      api.get<Paginated<Receiving>>('/receivings', {
        personId: doc.personId ?? undefined,
        search: search || undefined,
        perPage: 20,
      }),
    select: (r) => r.data,
  });

  const mutation = useMutation({
    mutationFn: () => api.post(`/fiscal-documents/${doc.id}/receivings`, { receivingId }),
    onSuccess: onLinked,
  });

  const jaVinculados = new Set(doc.receivings.map((l) => l.receivingId));

  return (
    <Modal
      open
      onClose={onClose}
      wide
      title="Vincular recebimento"
      description="Qual entrada de portaria esta nota cobre"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={!receivingId}
            loading={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            Vincular
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}

      <FormSection>
        <Field label="Buscar recebimento" htmlFor="vinc-rec-busca">
          <input
            id="vinc-rec-busca"
            className="input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="REC-2026-000012 ou pedido"
          />
        </Field>
      </FormSection>

      {query.isPending ? (
        <LoadingState label="Buscando recebimentos…" />
      ) : query.isError ? (
        <ErrorState message={errorMessage(query.error)} />
      ) : query.data.length === 0 ? (
        <p className="muted" style={{ fontSize: 12.5 }}>
          Nenhum recebimento encontrado.
        </p>
      ) : (
        <div role="radiogroup" aria-label="Recebimentos" style={{ display: 'grid', gap: 8 }}>
          {query.data.map((r) => {
            const ativo = r.id === receivingId;
            const jaTem = jaVinculados.has(r.id);
            return (
              <button
                key={r.id}
                type="button"
                role="radio"
                aria-checked={ativo}
                disabled={jaTem}
                onClick={() => setReceivingId(r.id)}
                className="card"
                style={{
                  textAlign: 'left',
                  padding: 12,
                  cursor: jaTem ? 'default' : 'pointer',
                  opacity: jaTem ? 0.6 : 1,
                  borderColor: ativo ? T.mareDark : T.line,
                  background: ativo ? T.mareTint : undefined,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <strong className="mono" style={{ fontSize: 13 }}>
                    {r.code}
                  </strong>
                  <span className="mono" style={{ fontSize: 12 }}>
                    {formatCents(r.totalCents)}
                  </span>
                </div>
                <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                  {jaTem ? 'já vinculado · ' : ''}
                  {blank(r.purchaseOrderCode)} · {formatDate(r.receivedAt)}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </Modal>
  );
}

function NovaDivergenciaModal({
  doc,
  onClose,
  onCreated,
}: {
  doc: FiscalDocument;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [type, setType] = useState('OUTRO');
  const [documentItemId, setDocumentItemId] = useState('');
  const [description, setDescription] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      api.post(`/fiscal-documents/${doc.id}/divergences`, {
        type,
        documentItemId: documentItemId || null,
        description: description.trim(),
      }),
    onSuccess: onCreated,
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Abrir divergência"
      description="O que não bate entre a nota, o pedido e o que chegou"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={description.trim().length < 5}
            loading={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            Abrir
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}

      <FormSection>
        <Field label="Tipo" htmlFor="nova-div-tipo">
          <select
            id="nova-div-tipo"
            className="input"
            value={type}
            onChange={(e) => setType(e.target.value)}
          >
            {Object.entries(FISCAL_DIVERGENCE_TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </Field>

        {doc.items.length > 0 && (
          <Field label="Linha da nota (opcional)" htmlFor="nova-div-item">
            <select
              id="nova-div-item"
              className="input"
              value={documentItemId}
              onChange={(e) => setDocumentItemId(e.target.value)}
            >
              <option value="">A nota inteira</option>
              {doc.items.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.sequence} · {item.description}
                </option>
              ))}
            </select>
          </Field>
        )}

        <Field label="O que aconteceu" htmlFor="nova-div-desc">
          <textarea
            id="nova-div-desc"
            className="input"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="O fornecedor cobrou frete que não estava no pedido"
          />
        </Field>
      </FormSection>
    </Modal>
  );
}
